import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, List, Modal, Pagination, Spin, Empty } from 'antd';
import { NotificationOutlined, CalendarOutlined } from '@ant-design/icons';
import AuthService from '../../services/authService';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import dayjs from 'dayjs';

const { Text } = Typography;

const PAGE_SIZE = 5;

const TopTag = () => (
    <span
        style={{
            background: '#fff7e6',
            color: '#fa8c16',
            border: '1px solid #ffd591',
            fontSize: 11,
            fontWeight: 600,
            padding: '1px 6px',
            borderRadius: 4,
            letterSpacing: '0.3px',
            flexShrink: 0,
        }}
    >
        TOP
    </span>
);

const NewTag = () => (
    <span
        style={{
            background: '#fff1f0',
            color: '#f5222d',
            border: '1px solid #ffa39e',
            fontSize: 11,
            fontWeight: 600,
            padding: '1px 6px',
            borderRadius: 4,
            letterSpacing: '0.3px',
            flexShrink: 0,
        }}
    >
        NEW
    </span>
);

const DashBoard = () => {
    const [notices, setNotices] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [activeNotice, setActiveNotice] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [pinnedDetails, setPinnedDetails] = useState({});

    const fetchNotices = useCallback(async (p = 1) => {
        setLoading(true);
        try {
            const res = await AuthService.authAxios.get('/notices', {
                params: { page: p, limit: PAGE_SIZE },
            });
            const list = res.data.list || [];
            setNotices(list);
            setTotal(res.data.total || 0);

            const expandable = list.filter(n => n.is_notice);
            if (expandable.length > 0) {
                const details = {};
                await Promise.all(
                    expandable.map(async n => {
                        try {
                            const dr = await AuthService.authAxios.get(`/notices/${n.id}`);
                            details[String(n.id)] = dr.data;
                        } catch {
                            details[String(n.id)] = null;
                        }
                    })
                );
                setPinnedDetails(details);
            }
        } catch {
            setNotices([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNotices(page);
    }, [fetchNotices, page]);

    const openNotice = async id => {
        setModalOpen(true);
        setDetailLoading(true);
        try {
            const res = await AuthService.authAxios.get(`/notices/${id}`);
            setActiveNotice(res.data);
        } catch {
            setActiveNotice(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeModal = () => {
        setModalOpen(false);
        setActiveNotice(null);
    };

    const isNew = date => dayjs().diff(dayjs(date), 'day') <= 7;

    const pinnedNotices = notices.filter(n => n.is_notice);
    const regularNotices = notices.filter(n => !n.is_notice);

    return (
        <div style={{ padding: '24px' }}>
            <Card
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <NotificationOutlined
                            style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15 }}
                        />
                        <span style={{ fontWeight: 600, fontSize: 15, color: '#fff' }}>
                            공지사항
                        </span>
                    </div>
                }
                style={{
                    borderRadius: 12,
                    boxShadow: '0 2px 16px rgba(0,0,0,0.10)',
                    border: 'none',
                    overflow: 'hidden',
                }}
                styles={{
                    header: {
                        background: '#001529',
                        border: 'none',
                        padding: '0 24px',
                        minHeight: 52,
                    },
                    body: { padding: 0 },
                }}
            >
                <Spin spinning={loading}>
                    {notices.length === 0 && !loading ? (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="등록된 공지사항이 없습니다."
                            style={{ padding: '48px 0' }}
                        />
                    ) : (
                        <>
                            {/* 펼치기 공지 */}
                            {pinnedNotices.map((item, idx) => (
                                <div
                                    key={item.id}
                                    style={{
                                        borderBottom:
                                            idx < pinnedNotices.length - 1 ||
                                            regularNotices.length > 0
                                                ? '1px solid #f0f0f0'
                                                : 'none',
                                    }}
                                >
                                    {/* 제목 행 */}
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            padding: '13px 24px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #f0f0f0',
                                            background: '#fff',
                                            transition: 'background 0.15s',
                                        }}
                                        onClick={() => openNotice(item.id)}
                                        onKeyDown={e => e.key === 'Enter' && openNotice(item.id)}
                                        onMouseEnter={e =>
                                            (e.currentTarget.style.background = '#f5f7ff')
                                        }
                                        onMouseLeave={e =>
                                            (e.currentTarget.style.background = '#fff')
                                        }
                                    >
                                        {item.is_top ? <TopTag /> : null}
                                        {isNew(item.created_at) ? <NewTag /> : null}
                                        <Text
                                            strong
                                            style={{ fontSize: 15, flex: 1, color: '#0d1b2a' }}
                                        >
                                            {item.title}
                                        </Text>
                                        <Text
                                            type="secondary"
                                            style={{
                                                fontSize: 12,
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <CalendarOutlined style={{ marginRight: 4 }} />
                                            {dayjs(item.created_at).format('YYYY.MM.DD')}
                                        </Text>
                                    </div>

                                    {/* 내용 */}
                                    <div style={{ padding: '20px 28px', background: '#fafbfc' }}>
                                        {pinnedDetails[String(item.id)] === undefined ? (
                                            <div style={{ textAlign: 'center', padding: '12px 0' }}>
                                                <Spin />
                                            </div>
                                        ) : pinnedDetails[String(item.id)] === null ? (
                                            <Empty description="내용을 불러올 수 없습니다." />
                                        ) : (
                                            <div
                                                className="notice-content"
                                                dangerouslySetInnerHTML={{
                                                    __html: pinnedDetails[String(item.id)].content,
                                                }}
                                                style={{
                                                    fontSize: 14,
                                                    lineHeight: 1.9,
                                                    wordBreak: 'break-word',
                                                    color: '#333',
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}

                            {/* 일반 공지 리스트 */}
                            {regularNotices.length > 0 && (
                                <>
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={regularNotices}
                                        renderItem={item => (
                                            <List.Item
                                                actions={[
                                                    <Text
                                                        key="date"
                                                        type="secondary"
                                                        style={{
                                                            fontSize: 12,
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        <CalendarOutlined
                                                            style={{ marginRight: 4 }}
                                                        />
                                                        {dayjs(item.created_at).format(
                                                            'YYYY.MM.DD'
                                                        )}
                                                    </Text>,
                                                ]}
                                                style={{
                                                    padding: '13px 24px',
                                                    cursor: 'pointer',
                                                    transition: 'background 0.15s',
                                                }}
                                                onMouseEnter={e =>
                                                    (e.currentTarget.style.background = '#f5f7ff')
                                                }
                                                onMouseLeave={e =>
                                                    (e.currentTarget.style.background =
                                                        'transparent')
                                                }
                                                onClick={() => openNotice(item.id)}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        minWidth: 0,
                                                        flex: 1,
                                                    }}
                                                >
                                                    {item.is_top ? <TopTag /> : null}
                                                    {isNew(item.created_at) ? <NewTag /> : null}
                                                    <Text
                                                        strong
                                                        style={{
                                                            fontSize: 15,
                                                            color: '#0d1b2a',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            flex: 1,
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        {item.title}
                                                    </Text>
                                                </div>
                                            </List.Item>
                                        )}
                                    />
                                    {total > PAGE_SIZE && (
                                        <div
                                            style={{
                                                padding: '12px 24px',
                                                textAlign: 'right',
                                                borderTop: '1px solid #f0f0f0',
                                            }}
                                        >
                                            <Pagination
                                                current={page}
                                                total={total}
                                                pageSize={PAGE_SIZE}
                                                size="small"
                                                showSizeChanger={false}
                                                onChange={p => setPage(p)}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Spin>
            </Card>

            {/* 공지 상세 모달 */}
            <Modal
                open={modalOpen}
                onCancel={closeModal}
                footer={null}
                title={
                    activeNotice ? (
                        <div>
                            <div
                                style={{
                                    fontWeight: 700,
                                    fontSize: 16,
                                    lineHeight: 1.5,
                                    color: '#0d1b2a',
                                }}
                            >
                                {activeNotice.title}
                            </div>
                            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                                <CalendarOutlined style={{ marginRight: 4 }} />
                                {dayjs(activeNotice.created_at).format('YYYY.MM.DD HH:mm')}
                            </Text>
                        </div>
                    ) : (
                        '공지사항'
                    )
                }
                width={720}
                centered
                styles={{ body: { padding: '24px', maxHeight: '60vh', overflowY: 'auto' } }}
            >
                {detailLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin size="large" />
                    </div>
                ) : activeNotice ? (
                    <div
                        className="notice-content"
                        dangerouslySetInnerHTML={{ __html: activeNotice.content }}
                        style={{
                            fontSize: 14,
                            lineHeight: 1.9,
                            wordBreak: 'break-word',
                            color: '#333',
                        }}
                    />
                ) : (
                    <Empty description="내용을 불러올 수 없습니다." />
                )}
            </Modal>

            <KakaoFloatButton />
        </div>
    );
};

export { DashBoard };
